import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { BookOpen, FileText, MapPinned, HeartHandshake, Briefcase, Heart, ShieldCheck, ArrowRight, Sparkles, MapPin } from 'lucide-react'
import { BottomNavigator } from '@/components/BottomNavigator'
import { spacing } from '@/theme'
import { api } from '@/api/axios'
import { useEffect, useState } from 'react'

type InteractionModule = {
  id: string
  key: string
  title: string
  description: string | null
  image: string | null
  url: string | null
  cities: string | string[] | null
  route: string | null
  order: number
  isActive: boolean
}

export const toArray = (v: string | string[] | null | undefined): string[] => {
  if (!v) return []
  if (Array.isArray(v)) return v
  try {
    const parsed = JSON.parse(v)
    if (Array.isArray(parsed)) return parsed.map(String)
  } catch {
    // legacy single string
  }
  return [v]
}

const MODULE_CONFIG: Record<string, { icon: any; accent: string; benefit: string }> = {
  escuela: { icon: BookOpen, accent: '#2563eb', benefit: 'Contenido practico' },
  'ofertas-laborales': { icon: Briefcase, accent: '#3b82f6', benefit: 'Empleo' },
  publicaciones: { icon: FileText, accent: '#f59e0b', benefit: 'Contenido verificado' },
  'nuestros-aliados': { icon: HeartHandshake, accent: '#8b5cf6', benefit: 'Red de apoyo' },
  fundacion: { icon: Heart, accent: '#f43f5e', benefit: 'Donaciones' },
  seguros: { icon: ShieldCheck, accent: '#64748b', benefit: 'Proteccion' },
  'red-de-apoyo': { icon: MapPinned, accent: '#10b981', benefit: 'Comunidad local' },
}

export function InteraccionesMenuScreen() {
  const navigate = useNavigate()
  const [modules, setModules] = useState<InteractionModule[]>([])

  useEffect(() => {
    api.get('/interaction-modules/active').then((res) => {
      setModules(res.data.data || [])
    }).catch(() => {})
  }, [])

  const openModule = (module: InteractionModule) => {
    if (module.route) {
      navigate(module.route)
    } else {
      navigate(`/interacciones/items/${encodeURIComponent(module.key)}`)
    }
  }

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          padding: spacing[4],
          paddingBottom: '56px',
          background: 'radial-gradient(circle at 10% 20%, #1e3a8a 0%, #0f172a 42%, #020617 100%)',
        }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              borderRadius: 20,
              border: '1px solid rgba(147, 197, 253, 0.35)',
              background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.72) 0%, rgba(15, 23, 42, 0.7) 100%)',
              padding: spacing[4],
              marginBottom: spacing[4],
              color: '#e2e8f0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[2] }}>
              <Sparkles size={20} color="#93c5fd" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#bfdbfe', letterSpacing: 0.5 }}>
                HUB INTERACTIVO
              </span>
            </div>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800 }}>Interacciones</h1>
            <p style={{ margin: `${spacing[1]} 0 0`, color: '#cbd5e1', fontSize: 14, lineHeight: 1.45 }}>
              Explora aprendizaje, promociones y comunidades de apoyo desde un solo lugar.
            </p>

            {/* <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: spacing[2],
                marginTop: spacing[3],
              }}
            >
              <div style={{ borderRadius: 12, padding: spacing[2], background: 'rgba(15, 23, 42, 0.58)', border: '1px solid rgba(148, 163, 184, 0.28)' }}>
                <Clock3 size={14} color="#93c5fd" />
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#cbd5e1' }}>Actualizacion diaria</p>
              </div>
              <div style={{ borderRadius: 12, padding: spacing[2], background: 'rgba(15, 23, 42, 0.58)', border: '1px solid rgba(148, 163, 184, 0.28)' }}>
                <ShieldCheck size={14} color="#86efac" />
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#cbd5e1' }}>Contenido verificado</p>
              </div>
              <div style={{ borderRadius: 12, padding: spacing[2], background: 'rgba(15, 23, 42, 0.58)', border: '1px solid rgba(148, 163, 184, 0.28)' }}>
                <MapPinned size={14} color="#f0abfc" />
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#cbd5e1' }}>Cobertura Colombia</p>
              </div>
            </div> */}
          </motion.section>

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
            {modules.map((module, idx) => {
              const config = MODULE_CONFIG[module.key] || MODULE_CONFIG.publicaciones
              const Icon = config.icon
              const cities = toArray(module.cities)
              return (
                <motion.button
                  key={module.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08 }}
                  whileHover={{ y: -4, scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => openModule(module)}
                  style={{
                    textAlign: 'left',
                    borderRadius: 18,
                    overflow: 'hidden',
                    border: `1px solid ${config.accent}66`,
                    background: `linear-gradient(125deg, ${config.accent}22 0%, rgba(11, 17, 32, 0.95) 72%)`,
                    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.3)',
                    color: '#e2e8f0',
                    padding: 0,
                    cursor: (module.route || module.url) ? 'pointer' : 'default',
                  }}
                >
                  {module.image && (
                    <div style={{ position: 'relative', height: 120, overflow: 'hidden' }}>
                      <img
                        src={module.image}
                        alt={module.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(to top, rgba(11, 17, 32, 0.95) 0%, transparent 100%)' }} />
                    </div>
                  )}
                  <div style={{ padding: spacing[4] }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing[3] }}>
                      <div style={{ display: 'flex', gap: spacing[3], alignItems: 'center' }}>
                        <div
                          style={{
                            width: 54,
                            height: 54,
                            borderRadius: 14,
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor: '#ffffff14',
                            border: '1px solid #ffffff2e',
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={24} color="#e2e8f0" />
                        </div>
                        <div>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#dbeafe',
                              padding: '3px 8px',
                              borderRadius: 999,
                              backgroundColor: '#ffffff1a',
                              marginBottom: spacing[1],
                            }}
                          >
                            {config.benefit}
                          </div>
                          <h3 style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>{module.title}</h3>
                          <p style={{ margin: `${spacing[1]} 0 0`, fontSize: 13, lineHeight: 1.45, color: '#cbd5e1' }}>
                            {module.description}
                          </p>
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: spacing[1],
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#e2e8f0',
                          flexShrink: 0,
                        }}
                      >
                        Entrar
                        <ArrowRight size={18} />
                      </div>
                    </div>
                    {cities.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: spacing[3], paddingTop: spacing[3], borderTop: '1px solid rgba(148, 163, 184, 0.15)' }}>
                        {cities.slice(0, 5).map((c) => (
                          <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600, color: '#bfdbfe', background: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
                            <MapPin size={11} /> {c}
                          </span>
                        ))}
                        {cities.length > 5 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, color: '#94a3b8', padding: '3px 9px' }}>
                            +{cities.length - 5} más
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </motion.button>
              )
            })}
          </div>
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}
